import { useEffect, useMemo, useState } from "react";
import { groupBlocks, usedByOthers } from "@/lib/blocks";
import { api, SIZES } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import OrderModelBlock from "@/components/OrderModelBlock";
import CustomerPicker from "@/components/CustomerPicker";
import { detectKoli, groupByProduct } from "@/lib/koli";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Trash2, ChevronDown, ChevronUp, Pencil, Printer, Scissors } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2);
const emptyLine = (block = uid(), model = "") => ({ id: uid(), block, model, product_id: "", sizes: {}, koli_type_id: "", koli_count: "" });

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Bekliyor", cls: "bg-[#3B82F6]/20 text-[#1D4ED8] border-[#3B82F6]/40" },
    partial: { label: "Kısmi Sevk", cls: "bg-[#F59E0B]/20 text-[#B45309] border-[#F59E0B]/40" },
    completed: { label: "Tamamlandı", cls: "bg-[#047857]/20 text-[#047857] border-[#047857]/40" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${cfg.cls}`}>{cfg.label}</span>
  );
};

export default function Orders() {
  const { products, productById, koliTypes } = useData();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();

  const toCutting = (o) => {
    const by = groupByProduct(o.items, (it) => it.qty - (it.shipped_qty || 0));
    const prefill = Object.entries(by).map(([product_id, sizes]) => ({ product_id, sizes }));
    if (!prefill.length) { toast.error("Bu siparişin kalan adedi yok"); return; }
    navigate("/kesim", { state: { prefill, source: `${o.customer_name} siparişinden`, note: `Sipariş: ${o.customer_name}` } });
  };

  const load = async () => {
    const r = await api.get("/orders");
    setOrders(r.data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setEditingId(null); setCustomerId(""); setDeliveryDate(""); setNote(""); setLines([emptyLine()]); };

  const startEdit = (o) => {
    const byProduct = {};
    o.items.forEach((it) => {
      const model = productById(it.product_id)?.model_code || "";
      byProduct[it.product_id] = byProduct[it.product_id] || { ...emptyLine(`m-${model}`, model), product_id: it.product_id, sizes: {} };
      byProduct[it.product_id].sizes[it.size] = it.qty;
    });
    setEditingId(o.id); setCustomerId(o.customer_id); setDeliveryDate(o.delivery_date); setNote(o.note || "");
    Object.values(byProduct).forEach((l) => Object.assign(l, detectKoli(l.sizes, koliTypes)));
    setLines(Object.values(byProduct).length ? Object.values(byProduct) : [emptyLine()]);
    setOpen(true);
  };
  const updateLine = (id, patch) => setLines(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  const updateSize = (id, s, v) => setLines(prev => prev.map(l => l.id === id ? { ...l, koli_count: "", sizes: { ...l.sizes, [s]: v } } : l));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));
  const blocks = useMemo(() => groupBlocks(lines), [lines]);
  const usedModels = useMemo(() => usedByOthers(blocks), [blocks]);
  const setBlockModel = (block, model) => setLines(prev => {
    const rest = prev.filter((l) => l.block !== block);
    const first = prev.findIndex((l) => l.block === block);
    const fresh = { ...emptyLine(block, model) };
    const colors = products.filter((p) => p.model_code === model);
    if (colors.length === 1) fresh.product_id = colors[0].id;
    rest.splice(first, 0, fresh);
    return rest;
  });
  const addColor = (block, model) => setLines(prev => {
    const last = prev.map((l) => l.block).lastIndexOf(block);
    const next = [...prev]; next.splice(last + 1, 0, emptyLine(block, model)); return next;
  });
  const removeBlock = (block) => setLines(prev => prev.filter((l) => l.block !== block));

  const totalPairs = lines.reduce((sum, l) => sum + Object.values(l.sizes).reduce((a, b) => a + (parseInt(b) || 0), 0), 0);

  const submit = async () => {
    if (!customerId) { toast.error("Müşteri kartı seçin"); return; }
    if (!deliveryDate) { toast.error("Teslim tarihi zorunlu"); return; }
    const items = [];
    for (const l of lines) {
      if (!l.model) { toast.error("Her model için model kodu seçin"); return; }
      if (!l.product_id) { toast.error(`${l.model}: her satır için renk seçin`); return; }
      for (const s of SIZES) {
        const q = parseInt(l.sizes[s]) || 0;
        if (q > 0) items.push({ product_id: l.product_id, size: s, qty: q });
      }
    }
    if (!items.length) { toast.error("En az bir numara için adet giriniz"); return; }
    setSubmitting(true);
    try {
      const body = { customer_id: customerId, delivery_date: deliveryDate, note, items };
      if (editingId) await api.put(`/orders/${editingId}`, body);
      else await api.post("/orders", body);
      toast.success(editingId ? "Sipariş güncellendi" : "Sipariş oluşturuldu");
      reset(); setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata oluştu");
    } finally { setSubmitting(false); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Siparişi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Sipariş silindi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Müşteri Siparişleri</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <FileText className="w-7 h-7 text-[#D97706]" /> Siparişler
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Alınan siparişler, kalan adetler ve teslim takibi</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="order-new-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> Yeni Sipariş
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="order-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">{editingId ? "Siparişi Düzenle" : "Yeni Sipariş"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#6B5B4A] mb-1 block">Müşteri *</Label>
                <CustomerPicker value={customerId} onChange={setCustomerId} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#6B5B4A]">Teslim Tarihi *</Label>
                  <Input data-testid="order-date-input" type="date" value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B5B4A]">Not</Label>
                  <Input data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
                </div>
              </div>

              {blocks.map((bl, idx) => (
                <OrderModelBlock key={bl[0].block} idx={idx} lines={bl}
                  usedModels={usedModels[idx]}
                  onModel={(m) => setBlockModel(bl[0].block, m)} onUpdate={updateLine} onUpdateSize={updateSize}
                  onAddColor={() => addColor(bl[0].block, bl[0].model)} onRemoveLine={removeLine}
                  onRemoveBlock={blocks.length > 1 ? () => removeBlock(bl[0].block) : null} />
              ))}
              <Button variant="outline" onClick={addLine} data-testid="order-add-line"
                className="border-[#DCCFB8] bg-[#EFE6D6] text-[#B45309] hover:bg-[#FFFBF4]">
                <Plus className="w-4 h-4 mr-2" /> Model Ekle
              </Button>
            </div>
            <DialogFooter>
              <div className="flex items-center justify-between w-full">
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
                  <p className="font-mono text-xl font-bold text-[#B45309]" data-testid="order-total">{totalPairs} çift</p>
                </div>
                <Button onClick={submit} disabled={submitting} data-testid="order-submit-btn"
                  className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
                  {submitting ? "Kaydediliyor..." : editingId ? "Değişiklikleri Kaydet" : "Siparişi Oluştur"}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="orders-list">
        {orders.length === 0 && (
          <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-10 text-center text-[#8C7B68]">
            Henüz sipariş yok. &ldquo;Yeni Sipariş&rdquo; ile başlayın.
          </div>
        )}
        {orders.map((o) => {
          const total = o.items.reduce((a, b) => a + b.qty, 0);
          const shipped = o.items.reduce((a, b) => a + (b.shipped_qty || 0), 0);
          const remaining = total - shipped;
          const isOpen = !!expanded[o.id];
          return (
            <div key={o.id} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight">{o.customer_name}</h3>
                    <StatusBadge status={o.status} />
                  </div>
                  <p className="text-[11px] font-mono text-[#8C7B68] uppercase tracking-widest mt-1">
                    Teslim: {new Date(o.delivery_date).toLocaleDateString("tr-TR")} · Alındı: {new Date(o.created_at).toLocaleDateString("tr-TR")}
                  </p>
                </div>
                <div className="flex items-center gap-5">
                  <div className="text-right">
                    <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
                    <p className="font-mono text-lg font-bold">{total}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-mono uppercase text-[#047857] tracking-widest">Sevk</p>
                    <p className="font-mono text-lg font-bold text-[#047857]">{shipped}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-mono uppercase text-[#B45309] tracking-widest">Kalan</p>
                    <p className="font-mono text-lg font-bold text-[#B45309]" data-testid={`order-remaining-${o.id}`}>{remaining}</p>
                  </div>
                  <button onClick={() => setExpanded(e => ({ ...e, [o.id]: !e[o.id] }))} data-testid={`order-toggle-${o.id}`}
                    className="text-[#6B5B4A] hover:text-[#2B2118]">
                    {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <Link to={`/siparis-fis/${o.id}`} target="_blank" data-testid={`order-print-${o.id}`} title="Sipariş Formu Yazdır"
                    className="text-[#8C7B68] hover:text-[#B45309]"><Printer className="w-4 h-4" /></Link>
                  {remaining > 0 && (
                    <button onClick={() => toCutting(o)} data-testid={`order-to-cutting-${o.id}`} title="Kesime Aktar"
                      className="text-[#8C7B68] hover:text-[#B45309]"><Scissors className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => startEdit(o)} data-testid={`order-edit-${o.id}`} title="Düzenle"
                    className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteOrder(o.id)} data-testid={`order-delete-${o.id}`} title="Sil"
                    className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {isOpen && (
                <div className="mt-4 pt-4 border-t border-[#E8DDCA] overflow-x-auto">
                  <table className="w-full text-sm zs-table">
                    <thead>
                      <tr>
                        <th className="text-left py-2">Model Kodu</th>
                        <th className="text-left py-2">Renk</th>
                        <th className="text-left py-2">Taban</th>
                        <th className="text-center py-2">Numara</th>
                        <th className="text-right py-2">Adet</th>
                        <th className="text-right py-2">Sevk</th>
                        <th className="text-right py-2">Kalan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.items.map((it) => {
                        const p = productById(it.product_id);
                        return (
                          <tr key={`${it.product_id}-${it.size}`} className="border-b border-[#E8DDCA] last:border-0">
                            <td className="py-2 font-mono text-[#B45309]">{p?.model_code || "—"}</td>
                            <td className="py-2 text-[#6B5B4A]">{p?.model_color || "—"}</td>
                            <td className="py-2 text-[#6B5B4A] text-xs">{p?.sole_code ? `${p.sole_code} / ${p.sole_color}` : "—"}</td>
                            <td className="py-2 text-center font-mono">N°{it.size}</td>
                            <td className="py-2 text-right font-mono">{it.qty}</td>
                            <td className="py-2 text-right font-mono text-[#047857]">{it.shipped_qty || 0}</td>
                            <td className="py-2 text-right font-mono font-bold text-[#B45309]">{it.qty - (it.shipped_qty || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {o.note && <p className="text-xs text-[#8C7B68] italic mt-3">&ldquo;{o.note}&rdquo;</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
